import { Injectable } from '@angular/core';
import { InvoiceCharges, PaymentCreate, RecnPaymentMethodType, RecnTransaction } from '@tangilla/tg-ng-models';
import {
  PaymentCreditCardService,
  PaymentService,
  PaymentTelecheckService,
  RecnService
} from '@tangilla/tg-ng-payments';
import { SurveyService } from '../../../../../core/services/survey/survey.service';
import { VariablesService } from '../../../../../core/services/survey/variables.service';
import { QuestionsService } from '../../../../../core/services/survey/questions.service';
import { of } from 'rxjs';
import { ErrorsService } from '../../../../../core/services/utils/errors.service';
import { environment } from '../../../../../../environments/environment';
import { SurveyPayloadService } from '../../../../../core/services/survey/survey-payload.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentFormWidgetService {
  paymentErrorMessage?: string;
  telecheckDeclined?: boolean;

  constructor(private recnService: RecnService,
              private paymentService: PaymentService,
              private paymentTelecheckService: PaymentTelecheckService,
              private paymentCreditCardService: PaymentCreditCardService,
              private variablesService: VariablesService,
              private questionsService: QuestionsService,
              private errorsService: ErrorsService,
              private surveyService: SurveyService,
              private surveyPayloadService: SurveyPayloadService) {
  }

  getPaymentPayload(invoiceCharges: Array<InvoiceCharges>, paymentMethod: 'recn' | 'check', body?: any) {
    let paymentPayload: PaymentCreate = {
      payment_ref: null,
      payment_date: (new Date()).toISOString(),
      payment_amount: this.paymentService.invoicesTotal(invoiceCharges),
      invoices: this.paymentService.getInvoicesChargesInformation(invoiceCharges),
      payment_method: paymentMethod,
    };

    const personId = this.getPersonId();
    if (personId) {
      paymentPayload.person_id = personId;
    }

    if (paymentMethod === 'recn') {
      paymentPayload = {
        ...paymentPayload,
        type: 'recn',
        payment_method_ref: body ? this.paymentCreditCardService.getCCMaskCardNumberValue(body.number) : null
      };
    }
    if (paymentMethod === 'check') {
      paymentPayload = {
        ...paymentPayload,
        ...this.paymentTelecheckService.getPaymentCheckFields()
      };
    }

    return paymentPayload;
  }

  async doMakePayment(body: any, paymentPayload: any, billingYear: number, paymentType: 'recn' | 'check', createCcoRecord?: boolean) {
    this.recnService.meta = JSON.stringify(this.surveyPayloadService.getSurveyPayload());

    this.recnService.onRecnTransactionStartProcessing = (recnTransaction: RecnTransaction) => {
      if (createCcoRecord &&
        recnTransaction.member_nrds_id &&
        recnTransaction.member_nrds_id !== recnTransaction.default_member_nrds_id) {

        this.questionsService.setQuestionValueOrCreate({name: 'NRDS', value: recnTransaction.member_nrds_id});
      }
      return of(recnTransaction);
    };

    this.recnService.onPaymentCompleted = ({recnTransaction, paymentPayload}: any) => {
      this.processPaymentSuccessful(recnTransaction.ec_control_number, recnTransaction.recn_request_id);

      return of({
        ...body,
        payment_payload: paymentPayload
      });
    };

    this.recnService.onPaymentErrored = (paymentError: { error: any, recnRequestId: string | undefined }) => {
      this.processPaymentErrors(paymentError, paymentPayload, body, paymentType);
      return of(paymentError);
    };

    const memberNrdsId = this.surveyService.memberId || this.questionsService.nrds?.value;
    try {
      await this.recnService.processPayment(
        paymentType === 'check' ? RecnPaymentMethodType.Check : RecnPaymentMethodType.CreditCard,
        this.surveyService.personId ? this.surveyService.personId : undefined,
        memberNrdsId,
        paymentPayload,
        body,
        billingYear,
        createCcoRecord
      );
    } catch (error: any) {
      // Catching error, because it's needed here.
    }

    return;
  }

  async processPaymentErrors(paymentError: {
    error: any,
    recnRequestId: string | undefined
  }, paymentPayload: any, body: any, paymentType: 'recn' | 'check') {
    const errorCode = paymentError.error.error_code;
    const resultCode = paymentError.error.result_code;
    const resultMessage = paymentError.error.result_message;
    const errorMessage = paymentError.error.message;
    const errorStack = paymentError.error.stack;

    switch (resultCode) {
      case '7':
      case '8':
        if (paymentType === 'check') {
          this.telecheckDeclined = true;
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          this.surveyService.showNextButton(false);
        } else {
          this.processCreditCardDeclined(resultCode, resultMessage);
        }
        break;
      case '804':
      case '805':
        this.telecheckDeclined = true;
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        this.surveyService.showNextButton(false);
        break;
      case '13':
      case '6':
        this.processNotKnownErrors();
        break;
      default:
        const thrownError = resultCode ? `${resultCode}: ${resultMessage}` : errorCode ? `${errorCode}:  ${resultMessage}` : `message: ${errorMessage}, stack: ${errorStack}`;
        const error = {
          error: {
            url: `${environment.api.billing}/recn_transaction/${paymentError.recnRequestId}`,
            error: {
              status: resultCode || 400,
              message: thrownError,
            }
          },
          status: resultCode || 400,
          data: thrownError,
          body,
        };

        await this.errorsService.processError(error);
        break;
    }
  }

  processPaymentSuccessful(ecControlNumber: any, associationRequestId: any) {
    this.questionsService.ecControlNumber.value = ecControlNumber;
    this.questionsService.associationRequestID.value = associationRequestId;
    this.variablesService.setVariable('paymentOK', true);
    this.surveyService.surveyModel.doComplete();
  }

  processCreditCardDeclined(resultCode: string, resultMessage: string) {
    this.variablesService.setVariable('paymentOK', false);
    this.displayPaymentError(`<div class="error-title"><i class="fa-regular fa-circle-x error-icon"></i><h4>Payment Declined</h4></div><p>Payment Error (${resultCode}): ${resultMessage}<br />${resultCode === '7' || resultCode === '8' ? 'This means your transaction was Declined by the credit card company. If you would like to understand why, you will need to call the customer service number on the back of your card' : 'Please try and process your payment again'}.</p>`);
  }

  processNotKnownErrors() {
    this.variablesService.setVariable('paymentOK', false);
    this.displayPaymentError('<div class="error-title"><i class="fa-regular fa-circle-x error-icon"></i><h4>Transaction Error</h4></div><p>Please try and process your payment again.</p>');
  }

  displayPaymentError(message: string) {
    this.paymentErrorMessage = message;
  }

  private getPersonId() {
    const personId = this.surveyService.surveyModel.getValue('person_id');

    return personId && personId !== 'undefined' ? parseInt(personId) : null;
  }
}

